import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from 'react-router-dom';
import Routes from 'routes';
import ThemeCustomization from 'themes';
import ScrollTop from 'components/ScrollTop';
import authService from 'store/services/auth.service';
import { ThemeProvider, useThemeContext } from 'themes/ThemeContext';
import { useGetTenancyBasicsQuery, useGetFeatureSwitchesQuery } from 'store/services/tenancy.service';
import { useGetReportsBFeaturesQuery } from 'store/services/reportsBFeatures.service';
import { hydrateOverrides as hydrateReportsBOverrides } from 'pages/reports-b/featureFlags';
import { isValidHexCode } from 'utils/colorUtils';
import { setIsHierarchyTenancy, setFeatureSwitches, setFeatureSwitchesLoading, setTenancyBasics, setTenancyBasicsLoading } from 'store/reducers/tenancy';
import Maintenance from 'pages/extra-pages/Maintenance';

const AppContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { customColors, themeMode, updateThemeColors } = useThemeContext();
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { requiresMfaSetup } = useSelector((state) => state.auth);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasUpdatedColors, setHasUpdatedColors] = useState(false);
    const lastNavigationRef = useRef(null);
    const user = useMemo(() => {
        return isAuthenticated ? JSON.parse(localStorage.getItem('user')) : null;
    }, [isAuthenticated]);

    // query for redux state tenancy - centralized call
    const { data: tenancyBasics, isLoading: tenancyBasicsLoading } = useGetTenancyBasicsQuery(
        { tenancyId },
        { skip: !isAuthenticated || !tenancyId }
    );

    // Dispatch tenancyBasics to Redux when data arrives
    useEffect(() => {
        if (tenancyBasics) {
            dispatch(setTenancyBasics(tenancyBasics));
        }
        dispatch(setTenancyBasicsLoading(tenancyBasicsLoading));
    }, [tenancyBasics, tenancyBasicsLoading, dispatch]);

    // Check if user is viewing their own tenancy (JWT features apply)
    const isOwnTenancy = user && String(tenancyId) === String(user.tenancyId);

    // User's original tenancy query - only needed for admins viewing a different tenancy
    // Skip if: not authenticated, no user tenancyId, is full admin, OR tenancyId matches (same tenancy)
    const { data: userTenancyBasics, isLoading: isLoadingUserTenancy } = useGetTenancyBasicsQuery(
        { tenancyId: user?.tenancyId },
        {
            skip: !isAuthenticated || !user?.tenancyId || user?.role === 'Administrators' ||
                  String(tenancyId) === String(user?.tenancyId)
        }
    );

    // Feature switches: JWT values used for instant render, API fetch validates in background
    // For own tenancy: JWT renders immediately, then API corrects any drift
    // For other tenancy: API is the only source
    const {
        data: featureSwitchesData,
        isLoading: featureSwitchesLoading
    } = useGetFeatureSwitchesQuery(
        { tenancyId },
        { skip: !isAuthenticated || !tenancyId }
    );

    const jwtFeatureSwitches = user?.featureSwitches;

    // Step 1: For own tenancy, dispatch JWT values immediately for instant render (no popping)
    useEffect(() => {
        if (!isAuthenticated || !tenancyId) return;

        if (isOwnTenancy && jwtFeatureSwitches) {
            dispatch(setFeatureSwitches(jwtFeatureSwitches));
        } else if (!isOwnTenancy && !featureSwitchesData) {
            // Switched tenancy, API not yet returned - show loading state
            dispatch(setFeatureSwitchesLoading(true));
        }
    }, [isOwnTenancy, isAuthenticated, tenancyId, jwtFeatureSwitches, dispatch, featureSwitchesData]);

    // Step 2: When API response arrives, update Redux (validates JWT values for own tenancy,
    // or provides the only source for switched tenancy)
    useEffect(() => {
        if (featureSwitchesData && isAuthenticated && tenancyId) {
            const actualData = featureSwitchesData?.data || featureSwitchesData;
            dispatch(setFeatureSwitches(actualData));
        }
    }, [featureSwitchesData, isAuthenticated, tenancyId, dispatch]);

    // Reports-b sub-page feature flags: seed the featureFlags overrides from the backend
    // (DynamoDB via /dashboard/features) so they persist across reloads/machines/admins.
    const { data: reportsBFeaturesData } = useGetReportsBFeaturesQuery(tenancyId, {
        skip: !isAuthenticated || !tenancyId,
    });
    useEffect(() => {
        if (reportsBFeaturesData?.flags) hydrateReportsBOverrides(reportsBFeaturesData.flags);
    }, [reportsBFeaturesData]);

    const updateColors = useCallback(() => {
        if (tenancyBasics && !tenancyBasicsLoading && !hasUpdatedColors) {
            const newColors = {
                primary: isValidHexCode(tenancyBasics.primaryHex) ? tenancyBasics.primaryHex : customColors.primary,
                secondary: isValidHexCode(tenancyBasics.secondaryHex) ? tenancyBasics.secondaryHex : customColors.secondary,
                warning: isValidHexCode(tenancyBasics.tertiaryHex) ? tenancyBasics.tertiaryHex : customColors.warning
            };
            updateThemeColors(newColors);
            setHasUpdatedColors(true);
        }
    }, [tenancyBasics, tenancyBasicsLoading, hasUpdatedColors, updateThemeColors, customColors]);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser && isAuthenticated) { // Add isAuthenticated check
            const userData = JSON.parse(storedUser);
            const needsMfaSetup = userData.requiresMfaSetup || requiresMfaSetup;

            if (needsMfaSetup && location.pathname !== '/mfa-setup') {
                console.log('App.js: Redirecting to MFA setup (backup check)');
                navigate('/mfa-setup', { replace: true });
            }
        }
    }, [isAuthenticated, requiresMfaSetup, location.pathname, navigate]);

    useEffect(() => {
        const checkAuth = () => {
            console.log('=== APP.JS checkAuth ===');
            console.log('App: Current path:', location.pathname);
            console.log('App: localStorage user exists:', !!localStorage.getItem('user'));

            const loggedIn = authService.isLoggedIn();
            console.log('App: isLoggedIn result:', loggedIn);
            setIsAuthenticated(loggedIn);

            const publicPaths = [
                '/login',
                '/forgot-password',
                '/login/reset-password',
                '/universitycourse/takecourse',
                '/takescormcourse',
                '/campaignclicked',
                '/landingpage',
                '/sso/',
                '/oktasso_initiate',
                '/oktasso',
                '/uss-login',
                '/cn-sso',
                '/google-sso/',
                '/aliaslogin',
                '/mfa-setup',
                '/login-mfa'
            ];

            const currentPath = location.pathname;
            const isPublicPath = publicPaths.some(path =>
                path.endsWith('/') ? currentPath.startsWith(path) : currentPath === path || currentPath.startsWith(path + '/')
            );

            console.log('App: isPublicPath:', isPublicPath);
            console.log('App: lastNavigationRef:', lastNavigationRef.current);

            if (!loggedIn && !isPublicPath && lastNavigationRef.current !== '/login') {
                console.log('App: >>> REDIRECTING TO /login <<<');
                lastNavigationRef.current = '/login';
                navigate('/login');
            } else {
                console.log('App: No redirect needed');
            }
        };
        checkAuth();
    }, [navigate, location.pathname]);

    useEffect(() => {
        if (isAuthenticated && user && tenancyBasics && !tenancyBasicsLoading) {
            if (user.role === 'Administrators' || !userTenancyBasics) {
                dispatch(setIsHierarchyTenancy(tenancyBasics.isHierarchyTenancy));
            } else if (userTenancyBasics) {
                dispatch(setIsHierarchyTenancy(userTenancyBasics.isHierarchyTenancy));
            }
        }
    }, [tenancyBasics, userTenancyBasics, tenancyBasicsLoading, isAuthenticated, user, dispatch]);

    useEffect(() => {
        updateColors();
    }, [updateColors]);

    // Check for maintenance mode
    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

    // If maintenance mode is enabled, show maintenance page
    if (isMaintenanceMode) {
        return (
            <ThemeCustomization customColors={customColors} mode={themeMode}>
                <Maintenance />
            </ThemeCustomization>
        );
    }

    return (
        <ThemeCustomization customColors={customColors} mode={themeMode}>
            <ScrollTop>
                <Routes />
            </ScrollTop>
        </ThemeCustomization>
    );
};

const App = () => {
    return (
        <ThemeProvider>
            <AppContent />
        </ThemeProvider>
    );
};

export default App;